import axiosInstance from '../../../../shared/api/axiosInstance';
import type {
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  Contact,
  ContactCreate,
  ContactUpdate,
  DomainEnrichmentResult,
  CompanyLookupResult,
  ListOrganizationsParams,
  ListContactsParams,
  PaginatedResponse,
  RelatedCounts,
  RelatedDeal,
  RelatedTicket,
  Tier,
  TierCreate,
  TierUpdate,
} from '../types';

const BASE_URL = '/api/v1/master-data/contacts';

// Organization API
export const organizationApi = {
  list: async (params?: ListOrganizationsParams): Promise<PaginatedResponse<Organization>> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations`, { params });
    return {
      data: response.data.data,
      pagination: {
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      },
    };
  },

  get: async (id: string): Promise<Organization> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/${id}`);
    return response.data.data;
  },

  create: async (data: OrganizationCreate): Promise<Organization> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations`, data);
    return response.data.data;
  },

  update: async (id: string, data: OrganizationUpdate): Promise<Organization> => {
    const response = await axiosInstance.put(`${BASE_URL}/organizations/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/organizations/${id}`);
  },

  archive: async (id: string): Promise<Organization> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/${id}/archive`);
    return response.data.data;
  },

  unarchive: async (id: string): Promise<Organization> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/${id}/unarchive`);
    return response.data.data;
  },

  bulkDelete: async (ids: string[]): Promise<{ deleted: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/bulk-delete`, { ids });
    return response.data;
  },

  bulkArchive: async (ids: string[]): Promise<{ archived: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/bulk-archive`, { ids });
    return response.data;
  },

  getIndustries: async (): Promise<string[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/industries`);
    return response.data.data;
  },

  enrichFromDomain: async (domain: string): Promise<DomainEnrichmentResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/enrich-domain`, { domain });
    return response.data.data;
  },

  lookupCompany: async (query: string): Promise<CompanyLookupResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/company-lookup`, { query });
    return response.data.data;
  },

  matchByDomain: async (domain: string): Promise<Organization | null> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/match-domain`, {
      params: { domain },
    });
    return response.data.data;
  },

  checkDomain: async (domain: string, excludeCompanyId?: string): Promise<{ available: boolean; company_name: string | null }> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/check-domain`, {
      domain,
      exclude_company_id: excludeCompanyId,
    });
    return response.data.data;
  },

  cascadeRelationship: async (
    organizationId: string,
    contactRelationship: string[],
    contactIds?: string[],
  ): Promise<{ updated_count: number }> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/organizations/${organizationId}/cascade-relationship`,
      {
        contact_relationship: contactRelationship,
        contact_ids: contactIds,
      },
    );
    return response.data.data;
  },
};

// Contact API (uses /people endpoints on backend)
export const contactApi = {
  list: async (params?: ListContactsParams): Promise<PaginatedResponse<Contact>> => {
    const response = await axiosInstance.get(`${BASE_URL}/people`, { params });
    return {
      data: response.data.data,
      pagination: {
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      },
    };
  },

  get: async (id: string): Promise<Contact> => {
    const response = await axiosInstance.get(`${BASE_URL}/people/${id}`, {
      params: { include_company: true },
    });
    return response.data.data;
  },

  create: async (data: ContactCreate): Promise<Contact> => {
    const response = await axiosInstance.post(`${BASE_URL}/people`, data);
    return response.data.data;
  },

  update: async (id: string, data: ContactUpdate): Promise<Contact> => {
    const response = await axiosInstance.put(`${BASE_URL}/people/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/people/${id}`);
  },

  archive: async (id: string): Promise<Contact> => {
    const response = await axiosInstance.post(`${BASE_URL}/people/${id}/archive`);
    return response.data.data;
  },

  unarchive: async (id: string): Promise<Contact> => {
    const response = await axiosInstance.post(`${BASE_URL}/people/${id}/unarchive`);
    return response.data.data;
  },

  bulkDelete: async (ids: string[]): Promise<{ deleted: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/people/bulk-delete`, { ids });
    return response.data;
  },

  bulkArchive: async (ids: string[]): Promise<{ archived: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/people/bulk-archive`, { ids });
    return response.data;
  },
};

// Tier API
export const tierApi = {
  list: async (): Promise<Tier[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/tiers`);
    return response.data.data;
  },

  get: async (id: string): Promise<Tier> => {
    const response = await axiosInstance.get(`${BASE_URL}/tiers/${id}`);
    return response.data.data;
  },

  create: async (data: TierCreate): Promise<Tier> => {
    const response = await axiosInstance.post(`${BASE_URL}/tiers`, data);
    return response.data.data;
  },

  update: async (id: string, data: TierUpdate): Promise<Tier> => {
    const response = await axiosInstance.put(`${BASE_URL}/tiers/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/tiers/${id}`);
  },

  initializeDefaults: async (): Promise<Tier[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/tiers/initialize-defaults`);
    return response.data.data;
  },

  listCompanies: async (tierId: string, params?: { search?: string; offset?: number; limit?: number }): Promise<PaginatedResponse<Organization>> => {
    const response = await axiosInstance.get(`${BASE_URL}/tiers/${tierId}/organizations`, { params });
    return {
      data: response.data.data,
      pagination: {
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      },
    };
  },

  assignCompanies: async (tierId: string, organizationIds: string[]): Promise<{ data: Organization[]; updated_count: number }> => {
    const response = await axiosInstance.put(`${BASE_URL}/tiers/${tierId}/organizations`, { company_ids: organizationIds });
    return response.data;
  },

  removeCompany: async (tierId: string, organizationId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/tiers/${tierId}/organizations/${organizationId}`);
  },
};

// Backlinks API (related entities)
export const backlinksApi = {
  // Contact backlinks
  getContactRelatedCounts: async (contactId: string): Promise<RelatedCounts> => {
    const response = await axiosInstance.get(`${BASE_URL}/people/${contactId}/related-counts`);
    return response.data.data;
  },

  listContactDeals: async (
    contactId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<PaginatedResponse<RelatedDeal>> => {
    const response = await axiosInstance.get(`${BASE_URL}/people/${contactId}/related/deals`, { params });
    return {
      data: response.data.data,
      pagination: {
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      },
    };
  },

  listContactTickets: async (
    contactId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<PaginatedResponse<RelatedTicket>> => {
    const response = await axiosInstance.get(`${BASE_URL}/people/${contactId}/related/tickets`, { params });
    return {
      data: response.data.data,
      pagination: {
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      },
    };
  },

  // Organization backlinks
  getCompanyRelatedCounts: async (organizationId: string): Promise<RelatedCounts> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/${organizationId}/related-counts`);
    return response.data.data;
  },

  listCompanyDeals: async (
    organizationId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<PaginatedResponse<RelatedDeal>> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/${organizationId}/related/deals`, { params });
    return {
      data: response.data.data,
      pagination: {
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      },
    };
  },

  listCompanyTickets: async (
    organizationId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<PaginatedResponse<RelatedTicket>> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/${organizationId}/related/tickets`, { params });
    return {
      data: response.data.data,
      pagination: {
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      },
    };
  },
};
